import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ClientList from "@/components/therapist/ClientList";
import ConnectClientForm from "@/components/therapist/ConnectClientForm";
import JournalEntryList from "@/components/therapist/JournalEntryList";
import DashboardHeader from "@/components/therapist/DashboardHeader";
import { useTherapistClients } from "@/hooks/useTherapistClients";
import { useClientJournalEntries } from "@/hooks/useClientJournalEntries";
import { useDisplayFormatting } from "@/hooks/useDisplayFormatting";
import { sessionManager } from "@/utils/sessionManager";
import { FeedbackButton } from "@/components/FeedbackButton";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useIdleLogoutWithWarning } from "@/hooks/useIdleLogoutWithWarning";
import { AlertTriangle } from "lucide-react";

const TherapistDashboard = () => {
  const navigate = useNavigate();
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("clients");

  const { 
    roleData, 
    roleLoading, 
    clients, 
    clientsLoading, 
    refetchClients 
  } = useTherapistClients();

  const {
    journalEntries,
    entriesLoading,
    therapistNotes,
    handleNoteChange
  } = useClientJournalEntries(selectedClientId);

  const {
    formatDate,
    getClientDisplayName,
    getClientDetails
  } = useDisplayFormatting();

  // Idle logout hook for therapists only
  const { showWarning, countdown } = useIdleLogoutWithWarning({
    logout: async () => {
      await supabase.auth.signOut();
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      navigate('/auth');
    },
    idleDuration: 570000, // 9 minutes 30 seconds
    warningDuration: 30000, // 30 seconds warning
  });

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          // Clear any existing session data
          await supabase.auth.signOut();
          localStorage.removeItem('userRole');
          localStorage.removeItem('userId');
          navigate('/auth');
          return;
        }

        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'therapist')
          .maybeSingle();

        if (!roleData) {
          // Clear any existing session data
          await supabase.auth.signOut();
          localStorage.removeItem('userRole');
          localStorage.removeItem('userId');
          toast.error("You do not have permission to access this page");
          navigate('/auth');
          return;
        }

        // If we get here, the user is a valid therapist
        localStorage.setItem('userRole', 'therapist');
        localStorage.setItem('userId', user.id);
      } catch (error) {
        console.error("Error checking access:", error);
        // Clear any existing session data
        await supabase.auth.signOut();
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        toast.error("Error verifying access");
        navigate('/auth');
      }
    };

    checkAccess();
  }, [navigate]);

  const selectedClient = clients?.find(client => client.client_id === selectedClientId);

  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    setActiveTab('journal');
  };

  const handleReturnToClientList = () => {
    setSelectedClientId(null);
    setActiveTab('clients');
  };

  if (roleLoading || clientsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#E8F0F8] to-white">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA] mx-auto" />
          <p className="mt-4 text-[#5A636E]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!roleData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#E8F0F8] to-white">
        <div className="text-center">
          <p className="text-[#5A636E]">Access denied. Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full">
      {/* Idle warning modal */}
      <Dialog open={showWarning}>
        <DialogContent className="bg-gradient-to-br from-yellow-50 to-white border-yellow-200 shadow-2xl">
          <div className="flex flex-col items-center text-center space-y-4">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mb-2 animate-bounce" />
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-yellow-700">Are you still there?</DialogTitle>
              <DialogDescription className="text-base text-yellow-900">
                For your security, you will be logged out in
                <span className="mx-2 px-3 py-1 rounded-lg bg-yellow-100 text-yellow-700 font-extrabold text-xl border border-yellow-300 animate-pulse">
                  {countdown}
                </span>
                seconds due to inactivity.<br />
                <span className="text-sm text-yellow-700">Click or press any key to stay logged in.</span>
              </DialogDescription>
            </DialogHeader>
          </div>
        </DialogContent>
      </Dialog>
      {/* Full-screen blue gradient background */}
      <div className="fixed inset-0 w-full h-full bg-gradient-to-b from-[#E8F0F8] to-white z-0" />
      <div className="relative z-10 min-h-screen w-full">
        <div className="container mx-auto max-w-7xl px-4">
          <Card className="mb-8 shadow-lg backdrop-blur-sm border border-[#CCCCCC] bg-white">
            <CardContent className="p-0">
              <DashboardHeader />
              <div className="px-6 py-4">
                <Tabs 
                  defaultValue="clients" 
                  value={activeTab} 
                  onValueChange={setActiveTab}
                  className="animate-fadeIn"
                >
                  <TabsList className="grid w-full grid-cols-2 md:grid-cols-3 mb-8 bg-[#E8F0F8]">
                    <TabsTrigger 
                      value="clients" 
                      className="data-[state=active]:bg-white data-[state=active]:text-[#333B44]"
                    >
                      My Clients
                    </TabsTrigger>
                    <TabsTrigger 
                      value="connect" 
                      className="data-[state=active]:bg-white data-[state=active]:text-[#333B44]"
                    >
                      Connect
                    </TabsTrigger>
                    {selectedClientId && (
                      <TabsTrigger 
                        value="journal" 
                        className="data-[state=active]:bg-white data-[state=active]:text-[#333B44]"
                      >
                        Journal
                      </TabsTrigger>
                    )}
                  </TabsList>
                  <TabsContent value="clients">
                    <ClientList 
                      clients={clients}
                      isLoading={clientsLoading}
                      onClientSelect={handleClientSelect}
                      onConnectClient={() => setActiveTab('connect')}
                      getClientDisplayName={getClientDisplayName}
                      getClientDetails={getClientDetails}
                      formatDate={formatDate}
                      onRelationshipUpdate={refetchClients}
                    />
                  </TabsContent>
                  <TabsContent value="connect">
                    <ConnectClientForm onClientConnected={refetchClients} />
                  </TabsContent>
                  {selectedClientId && (
                    <TabsContent value="journal">
                      <JournalEntryList 
                        selectedClientId={selectedClientId}
                        journalEntries={journalEntries}
                        therapistNotes={therapistNotes}
                        entriesLoading={entriesLoading}
                        selectedClient={selectedClient}
                        onReturnToClientList={handleReturnToClientList}
                        onNoteChange={handleNoteChange}
                        getClientDisplayName={getClientDisplayName}
                        formatDate={formatDate}
                        onViewAll={() => navigate(`/therapist/client/${selectedClientId}/journal-entries`)}
                      />
                    </TabsContent>
                  )}
                </Tabs>
              </div>
            </CardContent>
          </Card>
        </div>
        <div style={{ position: 'fixed', bottom: 12, right: 16, zIndex: 50 }} className="text-xs text-[#5A636E] select-none pointer-events-none">
          V1.0.001
        </div>
      </div>
    </div>
  );
};

export default TherapistDashboard;
